// Medieval lofi / "tavern at night" (docs/AUDIO_DESIGN.md §12): a double-coursed lute fingerpicks and rolls modal triads
// (Aeolian and Dorian in minor, Mixolydian and Ionian in major, open fifths and sus chords, a Picardy third at the end),
// a bowed viol holds the roots, a recorder or wooden flute plays folk-shaped motifs with grace notes in the mode, and hand
// drums (frame drum, slap, tambourine) keep a soft swung lilt. Sometimes a hurdy-gurdy drone hums under it all.
import { rng } from '../../engine/core.js';
import { QUALITIES, bassNote, playedChords, trackProgs } from '../harmony.js';
import { createLute } from './medieval-lute.js';
import { MODES, createViol, createRecorder, createFrameKit } from './medieval-band.js';

const mod12 = n => ((n % 12) + 12) % 12;
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};

/* ---------------- lute voicings: a bass course + 3 upper courses ---------------- */
// Tones (semitones over the root): med = major triad, medm = minor, med5 = open fifth, medsus2 / medsus4. The engine's final
// tonic (m9 / maj9 / 69) becomes a triad: major in major keys, and in minor on Picardy tracks. Anything else is reduced to its
// triad. Upper courses: every order of the tones (open fifth: 5-R-5 or R-5-R) stacked upward from MIDI 50–66, top 60–72,
// span ≤ 14, neighbours ≥ a whole step apart (≥ a minor 3rd below MIDI 55), ≥ a 4th above a bass under 47 (else ≥ a minor
// 3rd); the bass course is the root at MIDI 41–53.
const TONES = { med: [0, 4, 7], medm: [0, 3, 7], med5: [0, 7], medsus2: [0, 2, 7], medsus4: [0, 5, 7] };
const tonesOf = (c, plan) => {
  if (TONES[c.q]) return TONES[c.q];
  if (c.final && plan.key.mode === 'minor') return plan.medieval && plan.medieval.picardy ? TONES.med : TONES.medm;
  const iv = QUALITIES[c.q] || TONES.med;
  return iv.includes(4) ? TONES.med : iv.includes(3) ? TONES.medm : TONES.med5;
};
const ORDERS = { 3: [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]], 2: [[1, 0, 1], [0, 1, 0]] };
const CANDS = new Map();                                     // `${root}:${tones}` → [[bass, u0, u1, u2]]
function candidates(root, tones) {
  const key = `${root}:${tones}`;
  if (CANDS.has(key)) return CANDS.get(key);
  const pcs = tones.map(i => mod12(root + i)), out = [], basses = [];
  for (let m = 41 + mod12(root - 41); m <= 53; m += 12) basses.push(m);
  for (const order of ORDERS[tones.length]) for (let u0 = 50 + mod12(pcs[order[0]] - 50); u0 <= 66; u0 += 12) {
    const u = [u0];
    for (let k = 1; k < 3; k++) { let m = u[k - 1] + 2; m += mod12(pcs[order[k]] - m); u.push(m); }
    if (u[2] < 60 || u[2] > 72 || u[2] - u[0] > 14 || (u[0] < 55 && u[1] - u[0] < 3)) continue;
    for (const b of basses) if (u[0] - b >= (b < 47 ? 5 : 3)) out.push([b, ...u]);
  }
  CANDS.set(key, out);
  return out;
}
/** Cost: Σ|Δ| of the upper courses + .4·|Δbass| + 2·max(0, |Δtop| − 3) + .5·|mean(upper) − home| + .3·|bass − 47|, home =
 *  63 + plan.register (−5..+5, so tracks don't all open on the same notes). The same chord keeps its voicing (re-plucks
 *  and 2-bar chords). */
export function luteVoicing(sp, prev, c, plan) {
  const root = mod12(plan.key.tonic + c.root), tones = tonesOf(c, plan), home = 63 + (plan.register || 0);
  if (prev && prev.length === 4 && mod12(prev[0]) === root) {
    const want = new Set(tones.map(i => mod12(root + i))), have = new Set(prev.map(mod12));
    if (want.size === have.size && [...want].every(pc => have.has(pc))) return prev;
  }
  let best = null, bc = Infinity;
  for (const x of candidates(root, tones)) {
    const [b, u0, u1, u2] = x;
    let k = 0.5 * Math.abs((u0 + u1 + u2) / 3 - home) + 0.3 * Math.abs(b - 47);
    if (prev && prev.length === 4) k += Math.abs(u0 - prev[1]) + Math.abs(u1 - prev[2]) + Math.abs(u2 - prev[3]) + 0.4 * Math.abs(b - prev[0]) + 2 * Math.max(0, Math.abs(u2 - prev[3]) - 3);
    if (k < bc) { bc = k; best = x; }
  }
  return best || [41 + mod12(root - 41), ...tones.concat([12]).slice(0, 3).map(i => 55 + mod12(root + i - 55)).sort((a, b) => a - b)];
}

/* ---------------- viol bass: sparse roots and fifths, bowed ---------------- */
// One pattern per track (another on 20 % of bars): root (held to the chord's end) · fifth (root, then the fifth on beat 3 of a
// whole-bar chord) · walk (root, fifth, and a step of the mode on beat 4 towards the next chord's root). A root on every
// chord, a fill's stop cuts the line; the break and the intro's last bar hold whole roots, the final one rings 28 steps.
const VIOL_ALT = { root: 1, fifth: 1, walk: 0.3 };
function violLine({ plan, st, b, mode, chords, nextChords, stop }) {
  const pc = c => mod12(plan.key.tonic + c.root), out = [], end = stop ?? 16;
  const add = (s, midi, vel, dur) => { if (s < end) out.push({ s, midi, vel, dur: Math.min(dur, stop != null ? end - s : dur) }); };
  if (mode === 'whole' || mode === 'last' || (mode === 'outro' && b.j === 2)) {
    for (const c of chords) { const m = bassNote(pc(c), st.prevBass); add(c.start * 4, m, 0.74, mode === 'outro' ? 28 : c.beats * 4 * 0.96); st.prevBass = m; }
    return out;
  }
  const pat = st.rS() < 0.2 ? pickW(st.rS, VIOL_ALT) : plan.medieval.viol;
  chords.forEach((c, k) => {
    const c0 = c.start * 4, c1 = c0 + c.beats * 4, root = bassNote(pc(c), st.prevBass), iv = QUALITIES[c.q] || [0, 7];
    const fifth = iv.includes(7) ? (root + 7 <= 50 ? root + 7 : root - 5) : root, last = k === chords.length - 1;
    const v0 = c.onset === false ? 0.62 : 0.8;
    if (pat === 'root' || c1 - c0 < 16) add(c0, root, v0, (c1 - c0) * 0.96);
    else if (pat === 'fifth' || !last || !nextChords) { add(c0, root, v0, 7.6); add(8, fifth, 0.7, 7.6); }
    else {                                                   // walk: root · fifth · a step towards the next root
      add(c0, root, v0, 7.6); add(8, fifth, 0.7, 3.8);
      const target = bassNote(pc(nextChords[0]), root), scale = MODES[plan.medieval.mode], inMode = m => scale.includes(mod12(m - plan.key.tonic));
      let a = target + (st.rS() < 0.5 ? -1 : 1);
      if (!inMode(a)) a = target + (a < target ? -2 : 2);
      if (a !== target && inMode(a)) add(12, a, 0.62, 3.8);
    }
    st.prevBass = root;
  });
  return out;
}

/* ---------------- melody: a folk-dance motif (dotted pairs, stepwise arches) in place of the jazzhop one ---------------- */
// Rhythm cells (16th steps over 2 bars) with dotted long-short pairs and 8th runs; the contour moves one scale step (p .7),
// two (p .2) or leaps a 3rd and steps back (p .1), turning around past ±3; the last note lasts 5–10 steps. The engine still
// picks the notes (the key's pentatonic, chord tones on beats 1 and 3); the recorder's ornaments add the mode's other tones.
const MCELLS = [[0, 3, 4, 8], [0, 4, 6, 8, 12], [0, 6, 8, 12], [2, 4, 6, 8, 14], [0, 3, 4, 6, 8], [0, 2, 4, 8, 11, 12], [0, 4, 8, 10, 12]];
const MTAILS = [[], [16], [16, 20], [16, 19, 20], [20], [16, 22]];
function folkMotif(r, key) {
  const steps = MCELLS[Math.floor(r() * MCELLS.length)].concat(MTAILS[Math.floor(r() * MTAILS.length)]).slice(0, 7), n = steps.length;
  const last = 5 + Math.floor(r() * 6), durs = steps.map((s, i) => (i < n - 1 ? Math.min(6, steps[i + 1] - s) : last));
  const contour = [0];
  let dir = r() < 0.65 ? 1 : -1, back = 0;
  for (let i = 1; i < n; i++) {
    const u = r();
    let mv;
    if (back) { mv = -back; back = 0; } else if (u < 0.7) mv = dir; else if (u < 0.9) mv = 2 * dir; else { mv = 2 * dir; back = dir; }
    contour.push(contour[i - 1] + mv);
    if (Math.abs(contour[i]) >= 3) dir = -Math.sign(contour[i]);
  }
  const vel = steps.map((s, i) => (i === 0 ? 0.8 : s % 4 === 0 ? 0.72 : 0.62 + 0.06 * r()));
  return { steps, durs, contour, vel, mode: key.mode };
}

/* ---------------- comping: lute cells of [step, length, velocity, pick] ---------------- */
// Voicing indices: 0 = the bass course (thumb), 1–3 = the upper courses (-1 = the top). Every cell sounds the bass on steps 0
// and 8, so the 2nd chord of a split bar has its root; notes are left to ring (the lute's own decay, or the next pluck on
// the same course, ends them).
const U = [1, 2, 3];
const COMPS = {
  slow: [[0, 8, 1], [8, 8, 0.7, [0, -1]]],                                                          // a roll, then thumb + top
  roll: [[0, 8, 1], [8, 8, 0.82]],                                                                  // rolled chords on 1 and 3
  arp: [[0, 16, 1, [0]], [2, 14, 0.7, [1]], [4, 12, 0.74, [2]], [6, 10, 0.8, [3]], [8, 8, 0.86, [0]], [10, 6, 0.7, [2]], [12, 4, 0.76, [3]], [14, 2, 0.66, [1]]],
  dance: [[0, 4, 1, [0]], [4, 4, 0.72, U], [8, 4, 0.9, [0]], [12, 4, 0.72, U]],                     // a pavane's thumb and fingers
  lilt: [[0, 4, 1, [0]], [4, 2, 0.72, U], [6, 2, 0.58, [2, 3]], [8, 4, 0.9, [0]], [12, 2, 0.72, U], [14, 2, 0.58, [2, 3]]],
  run: [[0, 4, 1, [0]], [1, 3, 0.6, [1]], [2, 2, 0.64, [2]], [3, 3, 0.68, [3]], [4, 4, 0.62, [2]], [6, 2, 0.6, [1]],
    [8, 4, 0.9, [0]], [9, 3, 0.6, [1]], [10, 2, 0.64, [2]], [11, 3, 0.68, [3]], [12, 4, 0.62, [2]], [14, 2, 0.6, [3]]],   // a prelude's 16ths
};

/* ---------------- drums: hand drums, soft and swung ---------------- */
// K frame drum "dum" · S slap "tek" · H tambourine (o = a shake) · R finger "ka" · X the tambourine shaken · T bodhrán tone
const GRIDS = {
  //          0123456789abcdef
  tavern: { K: 'x.........x.....', S: '....x.......x...', H: 's.s.a.s.s.s.a.s.' },                  // the lofi backbeat on skins
  maqsum: { K: 'x.......x.......', S: '..s...x.....x...', H: '....s.......s...', R: '.......s.......s' },   // dum tek-tek dum tek
  baladi: { K: 'x.s.....x.......', S: '......x.....x...', H: '....s.......s.o.' },                  // dum-dum tek dum tek, a shake
  sparse: { K: 'x.....s.........', S: '........x.......', X: '....s.......s...' },                  // halftime: a slap on 3
  break: { R: '....s.......s...', X: 's.......s.......' },
};
const GROOVES = {
  chill: { sparse: 2, tavern: 1, maqsum: 0.4 },
  slow: { tavern: 1.4, maqsum: 1, sparse: 0.6, baladi: 0.5 },
  fast: { tavern: 1.2, maqsum: 1.2, baladi: 0.8, sparse: 0.3 },
  upbeat: { tavern: 1, maqsum: 1.3, baladi: 1.1 },
};

export default {
  id: 'medieval', name: 'Medieval', desc: 'Lute, recorder and old modal tunes over a soft hand-drum beat',
  //            chill                          balanced                       upbeat
  vary: 'colour',
  energy: {
    stretch:    [0.5,                          0.35,                          0.2],
    bpm:        [[64, 72],                     [70, 80],                      [78, 90]],
    swing:      [[56, 60],                     [54, 58],                      [52, 56]],
    snareLag:   [[0.008, 0.016],               [0.005, 0.012],                [0.003, 0.008]],
    groove:     [[GROOVES.chill, GROOVES.chill], [GROOVES.slow, GROOVES.fast], [GROOVES.upbeat, GROOVES.upbeat]],
    drumLevel:  [0.7,                          0.8,                           0.88],
    kitLp:      [[4200, 6000],                 [5000, 7000],                  [5800, 8000]],
    epLp:       [[2600, 3600],                 [3000, 4400],                  [3500, 5000]],     // the lute bus low-pass
    strum:      [[0.03, 0.05],                 [0.022, 0.04],                 [0.018, 0.03]],    // the roll of a chord
    epVel:      [[0.5, 0.6],                   [0.54, 0.64],                  [0.56, 0.68]],
    comp: [
      { slow: [0.8, 1.4], roll: [0.6, 1.2], arp: [0.3, 0.8], dance: [0, 0.2], lilt: [0.1, 0.4], run: [0, 0] },
      { slow: [0.1, 0.4], roll: [0.4, 1], arp: [0.6, 1.2], dance: [0.2, 0.6], lilt: [0.3, 0.8], run: [0, 0.2] },
      { slow: [0, 0.1], roll: [0.2, 0.6], arp: [0.5, 1], dance: [0.5, 1], lilt: [0.4, 0.9], run: [0.1, 0.4] },
    ],
    push:       [0.05,                         0.1,                           0.18],
    legato:     [0.8,                          0.7,                           0.55],
    lead:       [0.78,                         0.85,                          0.9],
    softLead:   [0.6,                          0.55,                          0.5],              // odds of the recorder over the wooden flute
    tone:       [[4200, 6200],                 [5000, 7500],                  [6000, 8500]],
    wowCents:   [[6, 11],                      [5, 9],                        [4, 8]],
    dust:       [[1, 1.6],                     [0.8, 1.4],                    [0.6, 1.2]],
    introHats:  [0.3,                          0.45,                          0.6],
    leadFrom:   [2,                            2,                             1],
    hatsFirst:  [0.4,                          0.3,                           0.2],
    breakDrums: [0.35,                         0.55,                          0.8],
    breakLead:  [0.35,                         0.45,                          0.6],
    drone:      [0.45,                         0.3,                           0.15],             // odds of a drone (where the harmony allows)
    ornament:   [[0.15, 0.28],                 [0.2, 0.32],                   [0.22, 0.36]],     // odds of a cut or tap per melody note
    viol:       [{ root: 1, fifth: 0.6 },      { root: 0.8, fifth: 1, walk: 0.25 }, { root: 0.4, fifth: 1, walk: 0.7 }],
    // development (arrange.js BY_ENERGY): no lift (the drone and the mode); divisions on the tune now and then
    lift:       [0, 0, 0],
    solo:       [0.15, 0.2, 0.25],
  },
  qualities: { med: [0, 4, 7], medm: [0, 3, 7], med5: [0, 7, 12], medsus2: [0, 2, 7], medsus4: [0, 5, 7] },
  progressions: {
    major: {
      g1: [[0, 'med', 4], [10, 'med', 4], [5, 'med', 4], [0, 'med', 4]],                              // I · ♭VII · IV · I (Mixolydian)
      g2: [[0, 'med', 4], [5, 'med', 4], [0, 'med', 4], [7, 'med', 4]],                               // I · IV · I · V (passamezzo)
      g3: [[0, 'med', 4], [10, 'med', 4], [0, 'med', 4], [10, 'med', 2], [0, 'med5', 2]],             // I · ♭VII · I · ♭VII|I5
      g4: [[5, 'med', 4], [0, 'med', 4], [10, 'med', 4], [0, 'medsus4', 2], [0, 'med', 2]],           // IV · I · ♭VII · Isus4|I
      g5: [[0, 'med', 4], [7, 'med', 4], [9, 'medm', 4], [4, 'medm', 4]],                             // I · V · vi · iii (romanesca)
      g6: [[5, 'med', 4], [0, 'med', 4], [5, 'med', 2], [7, 'medsus4', 1], [7, 'med', 1], [0, 'med', 4]],   // IV · I · IV|V4-3 · I
    },
    minor: {
      a1: [[0, 'medm', 4], [10, 'med', 4], [8, 'med', 4], [10, 'med', 4]],                           // i · VII · VI · VII (Aeolian)
      a2: [[0, 'medm', 4], [5, 'med', 4], [0, 'medm', 4], [5, 'med', 4]],                            // i · IV · i · IV (Dorian)
      a3: [[0, 'medm', 4], [10, 'med', 4], [0, 'medm', 4], [7, 'medm', 4]],                          // i · VII · i · v
      a4: [[0, 'medm', 4], [3, 'med', 4], [10, 'med', 4], [0, 'med5', 4]],                           // i · III · VII · i5
      a5: [[0, 'medm', 4], [3, 'med', 4], [10, 'med', 4], [8, 'med', 2], [7, 'med', 2]],             // i · III · VII · VI|V (Greensleeves)
      a6: [[0, 'medsus2', 4], [10, 'med', 4], [5, 'med', 4], [0, 'medm', 4]],                        // isus2 · VII · IV · i (Dorian)
      a7: [[0, 'medm', 4], [7, 'med', 4], [0, 'medm', 4], [10, 'med', 4]],                           // i · V · i · VII (la folia)
    },
  },
  // the bar that ends progB when it repeats progA: IV · V4-3 (a Renaissance cadence) in major; iv · I (a plagal Picardy) in minor
  turnarounds: { major: [[5, 'med', 2], [7, 'medsus4', 1], [7, 'med', 1]], minor: [[5, 'medm', 2], [0, 'med', 2]] },
  grids: GRIDS,
  fills: {
    roll: { roll: [[12, 0.3], [13, 0.38], [14, 0.5], [15, 0.62]] },                                   // fingers rolling on the skin
    doum: { roll: [[13, 0.5], [14, 0.62], [15, 0.75]], voice: 'tom' },                                  // bodhrán pickups
    stop: { stop: 12 },                                                                                 // the band stops, the lute rings
    shake: { open: 14 },                                                                                // a tambourine shake
  },
  comps: COMPS,
  keysLevel: 1.8,

  plan(plan, { r, E }) {
    const rr = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
    const minor = plan.key.mode === 'minor', pcsOf = cs => new Set(cs.flatMap(c => QUALITIES[c.q].map(i => mod12(c.root + i))));
    const all = pcsOf(trackProgs(plan).flatMap(p => p.chords)), A = pcsOf(plan.progA.chords);   // every loop and pass it plays
    // the mode of the melody's ornaments follows the harmony: the Dorian 6th (IV) or the Aeolian ♭6 (VI) in minor, the
    // Mixolydian ♭7 or the Ionian 7 (V) in major; progA decides a tie, a coin a track with neither
    const [x, y] = minor ? [9, 8] : [10, 11], coin = r();
    const has = (s, p, q) => s.has(p) && !s.has(q);
    const useX = has(all, x, y) || (!has(all, y, x) && (has(A, x, y) || (!has(A, y, x) && coin < 0.5)));
    // a drone (tonic + fifth) only where no chord puts a semitone against it: tonic alone under a ♭VI, none under a V
    const droneOk = !all.has(1) && !all.has(11), fifthOk = !all.has(6) && !all.has(8), wantDrone = r() < E.drone;
    plan.medieval = {
      mode: minor ? (useX ? 'dorian' : 'aeolian') : (useX ? 'mixolydian' : 'ionian'),
      drone: wantDrone && droneOk ? (fifthOk ? [0, 7] : [0]) : null,
      picardy: minor && r() < 0.6, orn: rr(...E.ornament), viol: pickW(r, E.viol),
    };
    plan.viol = { lp: rr(700, 1000, 0) };
    const k = plan.drums.kit;
    plan.drums.kit = {
      kickF0: rr(100, 120, 1), kickF1: rr(58, 68, 1), kickDecay: rr(0.16, 0.24), slapBP: rr(1300, 1800, 0), slapDecay: rr(0.04, 0.06),
      jingle: rr(0.05, 0.08), lp: k.lp,
    };
    if (plan.lead) Object.assign(plan.lead, { timbre: plan.lead.timbre === 'soft' ? 'recorder' : 'wood', motif: folkMotif(r, plan.key) });
  },

  voicing: luteVoicing,
  bass: violLine,
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, sub = () => rng(Math.floor(ctx.r() * 4294967296));
    const ep = createLute(ac, dry, wet, plan, { ...o, cap: ctx.cap, r: sub(), log: ctx.log, stepDur: ctx.stepDur });
    ep.warm(trackChords(plan), 3);
    const lead = plan.lead ? createRecorder(ac, dry, wet, plan, { ...o, stepDur: ctx.stepDur, r: sub() }) : null;
    return { ep, bass: createViol(ac, dry, plan.viol, o), kit: createFrameKit(ac, dry, wet, plan.drums.kit, o), lead };
  },
};

/** the track's voicings, first bars first (progA, progB, the final tonic as in engine.js barChords), to render their
 *  courses ahead of time */
function trackChords(plan) {
  const out = [], fin = { root: 0, q: plan.key.mode === 'minor' ? 'm9' : 'maj9', final: true };
  let prev = null;
  for (const [c, P] of playedChords(plan, fin)) { prev = luteVoicing(null, prev, c, P); out.push(prev); }
  return out;
}
